import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { apiErrorMessage, PESAN_KODE_UMUM } from "../lib/inventoryApi";
const PESAN_KODE_WO = { ...PESAN_KODE_UMUM, REGION_SCOPE: "WO ini milik cabang lain. Kalau perlu, minta Kepala Cabang pemilik WO atau Owner." };
import { useAuth } from "../contexts/AuthContext";
import { FA_DETAIL_ROLES } from "../lib/roles";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { WOStatusBadge, WO_STATUS_TONE, woStageStatusClass, woStageStatusLabel, woStageIconClass } from "../components/etams/StatusBadge";
import { WOStatusBar } from "../components/etams/StatusBar";
import { useJudulHalaman } from "../components/etams/Breadcrumb";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { ArrowLeft, CheckCircle2, Circle, PlayCircle, MinusCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import RejectRemakeSection from "../components/reject_remake/RejectRemakeSection";
import SesuaikanFaWo from "../components/etams/SesuaikanFaWo";
import { fetchProductionStages, stageLabel, tahapDilewatiFaLama, TAHAP_TANPA_SERAH_TERIMA } from "../lib/productionStages";
import { formatDate } from "../lib/format";
import { jamPesanan, useStoreTimezones } from "../lib/timezone";
import { labelAlasanGeser } from "../lib/papanKerjaApi";
import { konfirmasi } from "../lib/dialogAplikasi";
import { FA_STATUS_LABEL } from "../lib/statusLabels";
import { labelPaket } from "../lib/faKonfigurasi";
import { teksWilayahCabang } from "../lib/istilahWilayah";
import { penjelasanAksiWo } from "../lib/aksiWo";

// Peta status pindah ke `WO_STATUS_TONE` di components/etams/StatusBadge
// (KONTRAS-01 Batch 11), dilebur dengan kembarannya di WOList.

// Batch 2.2: STAGE_LABEL DIHAPUS. Nama tahap datang dari master lewat
// lib/productionStages.js — dulu konstanta ini duplikat dari
// rejectRemakeApi.js dan keduanya memuat 5 tahap lama.

const fmtDur = (sec) => {
  if (sec == null) return "-";
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ${sec % 60}s`;
  return `${Math.floor(sec / 3600)}j ${Math.floor((sec % 3600) / 60)}m`;
};

function StageRow({ stage, label, canAct, isOwner, onStart, onComplete, onSkip, prevDone, jam }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (stage.status !== "IN_PROGRESS") return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [stage.status]);

  const [skipReason, setSkipReason] = useState("");
  const [openSkip, setOpenSkip] = useState(false);

  const iconMap = {
    PENDING:     <Circle className="h-5 w-5 text-muted-foreground" />,
    IN_PROGRESS: <PlayCircle className={`h-5 w-5 animate-pulse ${woStageIconClass("IN_PROGRESS")}`} />,
    COMPLETED:   <CheckCircle2 className={`h-5 w-5 ${woStageIconClass("COMPLETED")}`} />,
    SKIPPED:     <MinusCircle className={`h-5 w-5 ${woStageIconClass("SKIPPED")}`} />,
  };
  const elapsed = stage.status === "IN_PROGRESS" && stage.started_at
    ? Math.floor((now - new Date(stage.started_at).getTime()) / 1000)
    : null;

  return (
    <div className="border rounded-lg p-4 bg-card" data-testid={`stage-row-${stage.stage_code}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1">
          <div className="mt-1">{iconMap[stage.status]}</div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium">{stage.stage_order}. {label}</span>
              <Badge className={woStageStatusClass(stage.status)}>{woStageStatusLabel(stage.status)}</Badge>
            </div>
            <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
              {/* K235: tahap yang digerakkan serah terima tidak punya penanda orang (pelaku sistem). */}
              {stage.started_at && <div>Mulai: {jam(stage.started_at)}{!stage.started_by && <span data-testid={`stage-otomatis-mulai-${stage.stage_code}`}> · otomatis dari serah terima</span>}</div>}
              {stage.completed_at && <div>Selesai: {jam(stage.completed_at)}{!stage.completed_by && <span data-testid={`stage-otomatis-selesai-${stage.stage_code}`}> · otomatis dari serah terima</span>} · Durasi: {fmtDur(Math.floor((new Date(stage.completed_at) - new Date(stage.started_at)) / 1000))}</div>}
              {stage.status === "IN_PROGRESS" && elapsed != null && (
                <div className="text-info-soft-foreground font-mono">Berjalan: {fmtDur(elapsed)}</div>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          {canAct && stage.status === "PENDING" && (
            <Button
              size="sm"
              disabled={!prevDone}
              onClick={onStart}
              data-testid={`stage-start-${stage.stage_code}`}
              title={!prevDone ? "Tahap sebelumnya belum selesai." : ""}
            >Mulai</Button>
          )}
          {canAct && stage.status === "IN_PROGRESS" && (
            <Button size="sm" onClick={onComplete} data-testid={`stage-complete-${stage.stage_code}`}>Selesaikan</Button>
          )}
          {isOwner && stage.status === "PENDING" && (
            <AlertDialog open={openSkip} onOpenChange={setOpenSkip}>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="outline" data-testid={`stage-skip-${stage.stage_code}`}>Lewati</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Lewati {label}?</AlertDialogTitle>
                  <AlertDialogDescription>Hanya bisa dilakukan Owner. Berikan alasan (min 5 karakter).</AlertDialogDescription>
                </AlertDialogHeader>
                <Textarea value={skipReason} onChange={(e) => setSkipReason(e.target.value)} rows={3} placeholder="Alasan dilewati…" />
                <AlertDialogFooter>
                  <AlertDialogCancel>Batal</AlertDialogCancel>
                  <AlertDialogAction disabled={skipReason.trim().length < 5} onClick={() => { onSkip(skipReason); setOpenSkip(false); setSkipReason(""); }}>Lewati</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>
    </div>
  );
}

export default function WODetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile, isOwner, isKepalaCabang } = useAuth();
  const roleCode = profile?.role?.code;
  const isAdminProduksi = roleCode === "ADMIN_PRODUKSI";
  const canActStage = isOwner || isKepalaCabang || isAdminProduksi;

  const [wo, setWO] = useState(null);
  // T93: jam menurut zona cabang WO + label WIB/WITA (bukan zona peramban).
  useStoreTimezones();
  const jamWo = (d) => jamPesanan(d, wo?.store_id || wo?.store?.id, wo?.region_id || wo?.region?.id);
  // Remah roti: nomor WO sebagai butir terakhir, bukan uuid. Sebelum early return.
  useJudulHalaman(wo?.wo_number);
  const [loading, setLoading] = useState(true);
  const [cancelReason, setCancelReason] = useState("");
  const [openCancel, setOpenCancel] = useState(false);
  // Batch 2.2: master tahap untuk NAMA tampilan (GET /api/production-stages).
  // Sengaja dinamai `stageMaster`, bukan `stages` — `stages` di bawah adalah
  // tahap MILIK WO INI (salinan saat dikunci), dua hal yang berbeda.
  const [stageMaster, setStageMaster] = useState([]);
  useEffect(() => {
    fetchProductionStages().then(setStageMaster).catch((e) => { setStageMaster([]); toast.error(apiErrorMessage(e, "Daftar tahap produksi gagal dimuat — muat ulang halaman.")); });
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/wo/${id}`);
      setWO(r.data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal memuat WO.", PESAN_KODE_WO));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!wo || ["COMPLETED", "CANCELLED"].includes(wo.status)) return;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [wo?.status, load]); // eslint-disable-line

  const _act = async (fn, successMsg) => {
    try {
      await fn();
      toast.success(successMsg);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Aksi gagal.", PESAN_KODE_WO));
    }
  };

  // ⛔ PAGAR URUTAN — lahir dari PJ-10, 2 September 2026.
  //
  //    Serah terima tahap dan penyelesaian tahap adalah DUA mesin yang
  //    mengukur hal berbeda: yang satu perpindahan barang + UPAH BORONGAN,
  //    yang lain kemajuan pekerjaan. Keduanya berjalan bebas, dan urutan yang
  //    salah membuat upah NOL BISA DICATAT SELAMANYA:
  //
  //      tahap ditutup semua -> WO auto-COMPLETED -> serah terima DITOLAK
  //      ("WO ber-status COMPLETED tidak boleh menerima serah terima")
  //
  //    Ditemukan dengan menjalaninya: WOSUR-260902-25 kehilangan seluruh
  //    upahnya begitu enam tahap ditutup lebih dulu. WOSUR-260902-26 dengan
  //    urutan terbalik mencatat Rp50.000 dengan benar.
  //
  // ⚠ SENGAJA NOL DIJADIKAN PAGAR BACKEND. Menutup tahap tanpa serah terima
  //   adalah kontrak Fase 1C yang diuji eksplisit (test_phase1c_wp3a_wo_core
  //   test_7_full_completion: JAHIT ditutup tanpa satu pun serah terima, lalu
  //   WO WAJIB auto-COMPLETED). Mengubahnya bukan keputusan teknis — ia
  //   mengubah KAPAN sebuah WO boleh ditutup, dan itu milik Owner.
  //
  //   Yang dikerjakan di sini: membuat kekeliruannya mustahil dilakukan
  //   TANPA SADAR. Orangnya tetap boleh melanjutkan; ia cuma nol bisa lagi
  //   bilang tidak tahu.
  const _selesaikanTahap = async (stageCode) => {
    // LAYOUT/PRINT_SUBLIM nol pernah bisa diserahterimakan (Q22) — bertanya
    // "sisa" untuk keduanya = 24 pcs "belum diserahterimakan" yang menyesatkan.
    const faIds = TAHAP_TANPA_SERAH_TERIMA.includes(stageCode) ? [] : (wo?.source_fa_ids || []);
    let sisaTotal = 0;
    try {
      const hasil = await Promise.all(faIds.map((fa) =>
        api.get("/borongan/serah-terima/sisa", { params: { fa_id: fa, from_stage_code: stageCode } })
          .then((r) => Number(r.data?.sisa) || 0)
          .catch(() => 0)));
      sisaTotal = hasil.reduce((a, b) => a + b, 0);
    } catch { /* gagal cek = nol menghalangi; pagar ini pengingat, bukan kunci */ }

    if (sisaTotal > 0) {
      const lanjut = await konfirmasi(
        `${stageLabel(stageMaster, stageCode)}: masih ada ${sisaTotal} pcs yang BELUM diserahterimakan.\n\n` +
        `Kalau seluruh tahap ditutup lebih dulu, WO ini otomatis SELESAI dan serah terima ` +
        `setelahnya akan DITOLAK — upah borongan untuk ${sisaTotal} pcs itu tidak bisa dicatat lagi.\n\n` +
        `Catat serah terimanya dulu di menu Produksi → Serah Terima Tahap.\n\n` +
        `Tetap tutup tahap ini sekarang?`, { judul: "Serah terima belum lengkap", bahaya: true });
      if (!lanjut) return;
    }
    _act(() => api.post(`/wo/${id}/stages/${stageCode}/complete`),
         `${stageLabel(stageMaster, stageCode)} selesai.`);
  };

  if (loading) return <div className="p-6 text-muted-foreground">Memuat WO…</div>;
  if (!wo) return <div className="p-6 text-danger-soft-foreground">WO tidak ditemukan.</div>;

  const stages = [...(wo.stages || [])].sort((a, b) => a.stage_order - b.stage_order);

  return (
    <div className="space-y-6" data-testid="wo-detail-page">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate("/wo")}><ArrowLeft className="h-4 w-4 mr-1" /> Kembali</Button>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold font-mono">{wo.wo_number}</h1>
          <div className="flex items-center gap-2 mt-1">
            <WOStatusBadge value={wo.status} />
            <span className="text-xs text-muted-foreground">Dibuat {jamWo(wo.created_at)}</span>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load}><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
      </div>

      {/* Status bar tahapan WO (Tahap 5 ERP). Tahap PRODUKSI per stage sudah
          punya kartunya sendiri di bawah — ini alur STATUS WO-nya, dari draft
          sampai selesai, supaya orang tahu WO ini sedang di mana sebelum
          membaca enam kartu tahap. */}
      <WOStatusBar value={wo.status} />

      {/* Info + Approval Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-4 lg:col-span-2">
          <h2 className="font-medium mb-3">Info WO</h2>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-muted-foreground">Paket:</span> {labelPaket(wo.wo_type)}</div>
            {/* T73: satu label tempat — "Wilayah: Samarinda · Cabang: Balikpapan" (lib/istilahWilayah). */}
            <div className="col-span-2" data-testid="wo-wilayah-cabang">{teksWilayahCabang(wo.region?.name || wo.region_name, wo.store?.name || wo.store_name)}</div>
            <div><span className="text-muted-foreground">Total Atasan:</span> {wo.total_qty_atasan}</div>
            <div><span className="text-muted-foreground">Total Celana:</span> {wo.total_qty_celana}</div>
            <div><span className="text-muted-foreground">Dikunci pada:</span> {jamWo(wo.locked_at)}</div>
          </div>
          <div className="mt-4">
            <p className="text-xs text-muted-foreground mb-1">FA sumber</p>
            <div className="space-y-1">
              {/* K206 / VONIS-HAK-BACA-ISI-FA: rincian FA + nama customer hanya Owner/KC/CS. Admin Produksi/Admin Gudang
                  melihat nomor + status FA sumber tanpa tautan ke rincian FA (rutenya tertutup bagi mereka). */}
              {(wo.source_fas || []).map(f => {
                const isi = (
                  <>
                    <span className="font-mono">{f.fa_number}</span>
                    {f.customer_name && <span className="text-muted-foreground"> — {f.customer_name}</span>}
                    <Badge variant="outline" className="ml-2 text-xs">{FA_STATUS_LABEL[f.order_status] || f.order_status}</Badge>
                  </>
                );
                return FA_DETAIL_ROLES.includes(profile?.role?.code) ? (
                  <Link key={f.id} to={`/fa-orders/${f.id}`} className="block text-sm hover:bg-muted/50 rounded px-2 py-1">{isi}</Link>
                ) : (
                  <div key={f.id} className="block text-sm rounded px-2 py-1" data-testid={`wo-fa-sumber-${f.id}`}>{isi}</div>
                );
              })}
            </div>
          </div>
          {/* ⛔ K136 — JEJAK PENGGANTIAN FA, DI LAYAR WO-NYA.
              Owner 31 Agustus 2026: "iya harus terlihat di layar WO nya."

              Pembacanya adalah Kepala Cabang yang sedang memutuskan menyetujui,
              dan ia memutuskannya DI SINI — bukan di layar audit. Persetujuan
              yang nol tahu apa yang disetujuinya adalah stempel.

              ⚠ Ditaruh TEPAT DI BAWAH daftar Source FA, bukan di bagian
                terpisah di bawah: yang dibandingkan orang adalah "isinya
                sekarang" dengan "apa yang berubah", dan dua hal yang
                dibandingkan wajib terlihat bersamaan. */}
          {(wo.penyesuaian_fa || []).length > 0 && (
            <div className="mt-4 text-sm border-t pt-3">
              <p className="text-xs text-muted-foreground mb-2">
                FA pernah disesuaikan — {wo.penyesuaian_fa.length} perubahan
              </p>
              <div className="space-y-2">
                {wo.penyesuaian_fa.map((p) => (
                  <div key={p.id} className="rounded-md border border-border px-3 py-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge
                        variant="outline"
                        className={p.aksi === "HAPUS"
                          ? "text-xs text-danger-soft-foreground"
                          : "text-xs text-success-soft-foreground"}
                      >
                        {p.aksi}
                      </Badge>
                      <span className="font-mono text-xs">{p.fa_number}</span>
                      <span className="text-xs text-muted-foreground">
                        oleh {p.oleh} · {jamWo(p.dilakukan_pada)}
                      </span>
                    </div>
                    {/* Alasannya, apa adanya — ia yang jadi alasan tabel ini ada. */}
                    <p className="mt-1 whitespace-pre-wrap">{p.alasan}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ⛔ K172 G9 — RIWAYAT GESER TAHAP, di layar WO-nya (pola K136):
              siapa, kapan, dari tanggal, ke tanggal, alasan, ikut mundur.
              Judul B17 dan kolom B18 milik K172 §6. Baris `atas_nama_role`
              terisi = wewenang pinjaman (K140/C1): pelakunya tetap orang
              yang sungguhan menekan tombol, perannya yang dipinjam. */}
          {(wo.riwayat_geser || []).length > 0 && (
            <div className="mt-4 text-sm border-t pt-3" data-testid="riwayat-geser">
              <p className="text-xs text-muted-foreground mb-2">
                Riwayat geser — {wo.riwayat_geser.length}
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-muted-foreground">
                    <tr className="text-left">
                      <th className="py-1 pr-2 font-medium">Kapan</th>
                      <th className="py-1 pr-2 font-medium">Siapa</th>
                      <th className="py-1 pr-2 font-medium">Tahap</th>
                      <th className="py-1 pr-2 font-medium">Dari</th>
                      <th className="py-1 pr-2 font-medium">Ke</th>
                      <th className="py-1 pr-2 font-medium">Alasan</th>
                      <th className="py-1 font-medium">Ikut mundur</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wo.riwayat_geser.map((g) => (
                      <tr key={g.id} className="border-t border-border align-top">
                        <td className="py-1 pr-2 whitespace-nowrap">{jamWo(g.created_at)}</td>
                        <td className="py-1 pr-2">
                          {g.oleh || "?"}
                          {g.atas_nama_role && (
                            <Badge variant="outline" className="ml-1 text-[10px]">{String(g.atas_nama_role).replace(/_/g, " ")}</Badge>
                          )}
                        </td>
                        <td className="py-1 pr-2 whitespace-nowrap">{stageLabel(stageMaster, g.stage_code)}</td>
                        <td className="py-1 pr-2 whitespace-nowrap">{formatDate(g.dari_tanggal)}</td>
                        <td className="py-1 pr-2 whitespace-nowrap">
                          {formatDate(g.ke_tanggal)}
                          {g.melewati_deadline && (
                            /* B19 */
                            <span className="ml-1 rounded border border-danger-soft-border bg-danger-soft px-1 text-[10px] font-semibold text-danger-soft-foreground">
                              lewat deadline
                            </span>
                          )}
                        </td>
                        <td className="py-1 pr-2">
                          {labelAlasanGeser(g.alasan_kode)}
                          {g.alasan_teks ? ` — ${g.alasan_teks}` : ""}
                        </td>
                        <td className="py-1">{g.ikut_domino ? "✓" : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {wo.notes && (
            <div className="mt-4 text-sm border-t pt-3">
              <p className="text-xs text-muted-foreground mb-1">Catatan</p>
              <p className="whitespace-pre-wrap">{wo.notes}</p>
            </div>
          )}
        </Card>

        <Card className="p-4">
          <h2 className="font-medium mb-3">Aksi</h2>
          <div className="space-y-2">
            {wo.status === "DRAFT" && (isOwner || isAdminProduksi) && (
              <Button className="w-full" onClick={() => _act(
                () => api.post(`/wo/${id}/submit-approval`),
                "WO disubmit ke Kepala Cabang."
              )} data-testid="wo-submit-btn">Ajukan ke Kepala Cabang</Button>
            )}
            {/* K136 butir 2 — sesuaikan FA di draft (sensus pintu 23 Sep 2026: rutenya dulu tanpa kontrol). */}
            {wo.status === "DRAFT" && (isOwner || isAdminProduksi) && (
              <SesuaikanFaWo wo={{ ...wo, id }} onSelesai={load} />
            )}

            {wo.status === "PENDING_KEPALA_CABANG" && (isOwner || isKepalaCabang) && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button className="w-full" data-testid="wo-approve-kacab-btn">Setujui & Kunci WO</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Setujui & Kunci WO {wo.wo_number}?</AlertDialogTitle>
                    {/* Angka DITURUNKAN dari master, bukan diketik — dialog ini pernah
                        berbunyi "5 stages" saat yang lahir enam (diukur 2 Sep 2026). */}
                    {(() => {
                      const kerja = (stageMaster || []).filter((s) => s.code !== "GUDANG_JADI");
                      const dilewati = tahapDilewatiFaLama(wo.source_fas, stageMaster);
                      const total = Math.max(0, kerja.length) || 6;
                      if (!dilewati.length) {
                        return <AlertDialogDescription>WO akan dikunci dan {total} tahap produksi akan dibuat. Lanjutkan?</AlertDialogDescription>;
                      }
                      // FA lama (Q45): tahap sebelum tahap terakhirnya DILEWATI — KC harus
                      // membaca akibat ini SEBELUM menekan setuju, bukan sesudah dikunci.
                      return (
                        <AlertDialogDescription data-testid="wo-approve-fa-lama-dilewati">
                          WO ini dari FA lama. Dari {total} tahap, <b>{dilewati.length} akan DILEWATI</b> ({dilewati.map((s) => s.name).join(", ")}) dan <b>{total - dilewati.length} tahap</b> dibuat menunggu — produksi mulai dari tahap terakhir yang tercatat di FA lama. Lanjutkan?
                        </AlertDialogDescription>
                      );
                    })()}
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Batal</AlertDialogCancel>
                    <AlertDialogAction onClick={() => _act(
                      () => api.post(`/wo/${id}/approve-kepala-cabang`),
                      "WO dikunci. Produksi siap dimulai."
                    )}>Setujui</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}

            {wo.status === "LOCKED" && (isOwner || isAdminProduksi) && (
              <>
                <Button variant="outline" className="w-full" onClick={() => _act(
                  () => api.post(`/wo/${id}/start-production`),
                  "WO masuk fase produksi."
                )} data-testid="wo-start-prod-btn">Mulai Produksi</Button>
                <p className="text-xs text-muted-foreground">Atau langsung klik Mulai di tahap Layout. Serah terima tahap pertama juga memulai produksi otomatis.</p>
              </>
            )}

            {["DRAFT", "PENDING_KEPALA_CABANG"].includes(wo.status) && (isOwner || isKepalaCabang) && (
              <AlertDialog open={openCancel} onOpenChange={setOpenCancel}>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" className="w-full" data-testid="wo-cancel-btn">Batalkan WO</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Batalkan WO {wo.wo_number}?</AlertDialogTitle>
                    <AlertDialogDescription>Berikan alasan pembatalan (min 5 karakter). Pembatalan tidak bisa dikembalikan, kecuali oleh Owner.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <Textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} rows={3} placeholder="Alasan pembatalan…" />
                  <AlertDialogFooter>
                    <AlertDialogCancel>Batal</AlertDialogCancel>
                    <AlertDialogAction disabled={cancelReason.trim().length < 5} onClick={() => {
                      _act(() => api.post(`/wo/${id}/cancel`, { reason: cancelReason }), "WO dibatalkan.");
                      setOpenCancel(false); setCancelReason("");
                    }}>Batalkan WO</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}

            {/* Titipan auditor 27 Okt 2026: sesudah Mulai di tahap Layout kotak ini KOSONG — kini selalu ada
                penjelasan tindakan berikutnya bila tidak ada tombol untuk peran ini. */}
            {(() => {
              const t = penjelasanAksiWo({
                status: wo.status, stages, label: (k) => stageLabel(stageMaster, k),
                bisaAjukan: isOwner || isAdminProduksi, bisaSetujui: isOwner || isKepalaCabang,
                bisaMulai: isOwner || isAdminProduksi, bisaTahap: canActStage,
              });
              return t ? <p className="text-sm text-muted-foreground" data-testid="wo-aksi-penjelasan">{t}</p> : null;
            })()}

            {["COMPLETED", "CANCELLED"].includes(wo.status) && (
              <p className="text-sm text-muted-foreground text-center py-3">WO {WO_STATUS_TONE[wo.status]?.l.toLowerCase()}. Hanya baca.</p>
            )}
          </div>
        </Card>
      </div>

      {/* Stage Timeline */}
      <Card className="p-4">
        <h2 className="font-medium mb-1">Timeline Produksi</h2>
        {/* K235 (BUSINESS-114): tahap bergerak sendiri dari serah terima; tombol tetap ada sebagai cadangan. */}
        <p className="text-xs text-muted-foreground mb-4" data-testid="timeline-otomatis-hint">
          Tahap Potong sampai QC &amp; Packing bergerak sendiri dari serah terima: serah terima pertama memulai tahap
          penerima, dan tahap pemberi selesai begitu semua barangnya sudah diserahkan (QC &amp; Packing tetap menunggu
          checklist QC). Layout dan Print &amp; Sublim ditandai dengan tombol.
        </p>
        {stages.length === 0 ? (
          <p className="text-sm text-muted-foreground">Tahap produksi dibuat setelah WO disetujui Kepala Cabang.</p>
        ) : (
          <div className="space-y-2">
            {stages.map((s, i) => {
              const prev = i === 0 ? null : stages[i - 1];
              const prevDone = !prev || ["COMPLETED", "SKIPPED"].includes(prev.status);
              return (
                <StageRow
                  jam={jamWo}
                  key={s.id}
                  stage={s}
                  label={stageLabel(stageMaster, s.stage_code)}
                  canAct={canActStage && ["LOCKED", "IN_PROGRESS"].includes(wo.status)}
                  isOwner={isOwner && ["LOCKED", "IN_PROGRESS"].includes(wo.status)}
                  prevDone={prevDone}
                  onStart={() => _act(() => api.post(`/wo/${id}/stages/${s.stage_code}/start`), `${stageLabel(stageMaster, s.stage_code)} dimulai.`)}
                  onComplete={() => _selesaikanTahap(s.stage_code)}
                  onSkip={(reason) => _act(() => api.post(`/wo/${id}/stages/${s.stage_code}/skip`, { reason }), `${stageLabel(stageMaster, s.stage_code)} dilewati.`)}
                />
              );
            })}
          </div>
        )}
      </Card>

      {/* Reject / Remake (WP-3γ) — hidden untuk DRAFT/PENDING; role gated inside. */}
      <RejectRemakeSection wo={wo} onWOChanged={load} />
    </div>
  );
}
